/* ===================================================================
   OBEYTHECULTURE — FREE HANGER THRESHOLD ENGINE
   -------------------------------------------------------------------
   Feature 1: dynamic progress bar above the Shiprocket checkout button
   Feature 2: auto-add / auto-remove the free hanger at the threshold
              + unlock animation

   Loads AFTER script.js. Everything is inside an IIFE on purpose:
   script.js declares `const OTC_DISCOUNT_PRICE` at the top level of a
   classic script, so any top-level `const` sharing a name here would
   throw "Identifier has already been declared" and kill the file.
   =================================================================== */
(function () {
  'use strict';

  /* =================================================================
     ▼▼▼  CONFIG — EDIT THESE TWO VALUES  ▼▼▼
     ================================================================= */

  // 1. Replace with the numeric VARIANT id of your hanger product.
  //    Find it: Products → Hanger → Variant → the last number in the URL
  //    (…/variants/44123456789012). Use this variant ID for the hanger product
  const DUMMY_HANGER_VARIANT_ID = '45686971826311';

  // 2. Replace with your hanger image URL (Settings → Files → copy link).
  const HANGER_IMAGE_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><g fill="none" stroke="#a8813f" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M60 34a9 9 0 1 1 9-9"/><path d="M60 34 18 68a4 4 0 0 0 2.5 7.2h79a4 4 0 0 0 2.5-7.2Z"/></g><text x="60" y="99" font-family="Georgia,serif" font-size="9" fill="#a8813f" text-anchor="middle" letter-spacing="2">OBEYTHECULTURE</text></svg>');

  /* =================================================================
     ▲▲▲  END CONFIG  ▲▲▲
     ================================================================= */

  // Threshold in paise. Shopify stores money in minor units, so ₹4,000 = 400000.
  const THRESHOLD = 400000;

  // Must mirror OTC_DISCOUNT_PRICE in script.js (₹2,490). Kept in sync manually
  // because script.js declares it with `const`, which does not attach to window.
  const FLAT_PRICE = 249000;

  // Count the OTCSELECT flat price toward the threshold, so the bar always
  // agrees with the subtotal shown directly beneath it. Set false to gate on
  // the true (undiscounted) Shopify subtotal instead.
  const USE_DISCOUNTED_SUBTOTAL = true;

  const UNLOCK_SEEN_KEY = 'otc_hanger_unlock_seen';

  const money = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  });

  let syncing = false;   // re-entrancy guard: stops add → render → add loops
  let originalRenderCart = null;

  const isConfigured = () =>
    /^\d+$/.test(String(DUMMY_HANGER_VARIANT_ID).trim());

  const discountOn = () =>
    USE_DISCOUNTED_SUBTOTAL &&
    typeof window.isDiscountActive === 'function' &&
    window.isDiscountActive();

  /* ---------- cart helpers ---------- */

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not read the cart.');
    return res.json();
  }

  const isGift = (item) =>
    String(item.variant_id) === String(DUMMY_HANGER_VARIANT_ID) ||
    (item.properties && item.properties._free_gift === 'true');

  // Subtotal that counts toward the threshold. The gift is always excluded,
  // otherwise adding it would push the total up and the maths would chase itself.
  function eligibleSubtotal(cart) {
    const flat = discountOn();
    return cart.items.reduce((sum, item) => {
      if (isGift(item)) return sum;
      const unit = flat && item.price > FLAT_PRICE ? FLAT_PRICE : item.price;
      return sum + unit * item.quantity;
    }, 0);
  }

  async function addGift() {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        // `items` array form — the JSON payload can carry several line items at
        // once, which FormData cannot. Handy if you ever add a second freebie.
        items: [{
          id: Number(DUMMY_HANGER_VARIANT_ID),
          quantity: 1,
          // Underscore-prefixed properties are hidden from customer-facing
          // order displays, but still readable by you and by Shiprocket.
          properties: { _free_gift: 'true', _gift_reason: 'cart-over-4000' }
        }]
      })
    });
    if (!res.ok) throw new Error('Could not add the hanger.');
  }

  async function removeGift(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  /* ---------- the rule ----------
     At/above threshold: exactly one hanger in the cart.
     Below threshold: no hanger. Returns true if the cart was changed. */
  async function syncGift(cart) {
    if (!isConfigured()) return false;

    const giftLines = cart.items.filter(isGift);
    const qualifies = eligibleSubtotal(cart) >= THRESHOLD;

    try {
      if (qualifies && giftLines.length === 0) {
        await addGift();
        return true;
      }
      if (!qualifies && giftLines.length > 0) {
        for (const line of giftLines) await removeGift(line.key);
        try { sessionStorage.removeItem(UNLOCK_SEEN_KEY); } catch (e) {}
        return true;
      }
      // Someone bumped the gift quantity (cart page, back button). Force it to 1.
      if (qualifies && giftLines.length === 1 && giftLines[0].quantity > 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: giftLines[0].key, quantity: 1 })
        });
        return true;
      }
    } catch (err) {
      console.warn('[otc-free-gift]', err.message);
    }
    return false;
  }

  /* ---------- Feature 1: the progress bar ---------- */

  function hangerSVG() {
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"' +
      ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 6.5a2 2 0 1 1 2-2"/>' +
      '<path d="M12 6.5 3.2 13.4a1 1 0 0 0 .62 1.79h16.36a1 1 0 0 0 .62-1.79L12 6.5Z"/>' +
      '</svg>'
    );
  }

  function barMarkup(subtotal) {
    const remaining = Math.max(THRESHOLD - subtotal, 0);
    const unlocked = remaining === 0;
    const pct = Math.min((subtotal / THRESHOLD) * 100, 100);

    const label = unlocked
      ? 'Free hanger added'
      : 'Add ' + money.format(remaining / 100) + ' for a free hanger';

    return (
      '<div class="otc-fg' + (unlocked ? ' is-unlocked' : '') + '" role="status" aria-live="polite">' +
        '<div class="otc-fg-readout">' +
          '<span class="otc-fg-amount">' +
            money.format(subtotal / 100) +
            '<i>/</i>' + money.format(THRESHOLD / 100) +
          '</span>' +
          '<span class="otc-fg-label">' + label + '</span>' +
        '</div>' +
        '<div class="otc-fg-rail">' +
          '<div class="otc-fg-fill" style="width:' + pct.toFixed(2) + '%"></div>' +
          '<div class="otc-fg-hanger" style="left:' + pct.toFixed(2) + '%">' + hangerSVG() + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /* Inject the bar directly above the Shiprocket checkout button.
     renderCart() rebuilds .cart-panel with innerHTML on every call, so the bar
     has to be re-injected after each render rather than inserted once. */
  function injectBar(cart) {
    // Disabled as requested: user prefers the minimal progress bar in script.js
    return;
  }

  /* Present the gift as a locked row: no stepper, no Remove, price struck out. */
  function lockGiftRow(cart) {
    cart.items.forEach((item) => {
      if (!isGift(item)) return;
      const row = document.querySelector('.cart-item[data-key="' + item.key + '"]');
      if (!row) return;
      
      row.classList.add('otc-fg-giftrow');

      // The stepper and Remove button share one flex wrapper — swap it wholesale.
      const controls = row.querySelector('.cart-item-info > div:last-child');
      if (controls && !controls.querySelector('.otc-fg-gifttag')) {
        controls.innerHTML =
          '<span class="otc-fg-gifttag">' +
            '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">' +
            '<polyline points="20 6 9 17 4 12"/></svg>Gift · not removable' +
          '</span>';
      }

      const priceEl = row.querySelector('.cart-item-info > div:first-child > span:last-child');
      if (priceEl) {
        priceEl.className = 'otc-fg-giftprice';
        priceEl.innerHTML = item.price > 0
          ? '<s>' + money.format(item.price / 100) + '</s>FREE'
          : 'FREE';
      }
    });
  }

  /* ---------- Feature 2: unlock animation ---------- */

  function celebrate(imageUrl) {
    try {
      if (sessionStorage.getItem(UNLOCK_SEEN_KEY) === '1') return;
      sessionStorage.setItem(UNLOCK_SEEN_KEY, '1');
    } catch (e) {}

    document.querySelectorAll('.otc-fg-burst').forEach((n) => n.remove());

    const finalImage = imageUrl || HANGER_IMAGE_URL;

    const burst = document.createElement('div');
    burst.className = 'otc-fg-burst';
    burst.innerHTML =
      '<div class="otc-fg-burst-card">' +
        '<img class="otc-fg-burst-img" src="' + finalImage + '" alt="Premium branded hanger" ' +
             'onerror="this.style.display=\'none\'">' +
        '<p class="otc-fg-burst-kicker">Congratulations</p>' +
        '<h3 class="otc-fg-burst-title">Free Hanger Unlocked</h3>' +
        '<p class="otc-fg-burst-sub">A free limited edition hanger has been added to your bag!</p>' +
      '</div>';

    document.body.appendChild(burst);
    requestAnimationFrame(() => burst.classList.add('is-live'));

    setTimeout(() => {
      burst.classList.remove('is-live');
      setTimeout(() => burst.remove(), 500);
    }, 3200);
  }

  /* ---------- wire into renderCart ---------- */

  async function decorate() {
    let cart;
    try { cart = await getCart(); } catch (e) { return; }

    const hadGift = cart.items.some(isGift);

    if (!syncing) {
      syncing = true;
      try {
        const changed = await syncGift(cart);
        if (changed) {
          // Re-render with the ORIGINAL function — calling the wrapped one here
          // would recurse straight back into decorate().
          if (originalRenderCart) await originalRenderCart();
          cart = await getCart();
        }
      } finally {
        syncing = false;
      }
    }

    injectBar(cart);
    lockGiftRow(cart);

    // const giftItem = cart.items.find(isGift);
    // if (!hadGift && giftItem) celebrate(giftItem.image);
  }

  function patchRenderCart() {
    if (typeof window.renderCart !== 'function' || window.renderCart.__otcPatched) return false;

    originalRenderCart = window.renderCart;

    const wrapped = async function () {
      const out = await originalRenderCart.apply(this, arguments);
      await decorate();
      return out;
    };
    wrapped.__otcPatched = true;

    // `renderCart` is a top-level function declaration in script.js, so it lives
    // on window. Reassigning it means bare renderCart() calls elsewhere in the
    // theme resolve to the wrapper too.
    window.renderCart = wrapped;
    return true;
  }

  function boot() {
    if (!isConfigured()) {
      console.warn(
        '[otc-free-gift] DUMMY_HANGER_VARIANT_ID is still a placeholder. ' +
        'The progress bar will render but no hanger will be added.'
      );
    }

    // script.js may not have executed yet — poll briefly rather than assume.
    let tries = 0;
    (function waitForCart() {
      if (patchRenderCart()) {
        hydrate();
        return;
      }
      if (++tries < 60) setTimeout(waitForCart, 100);
      else console.warn('[otc-free-gift] renderCart() not found; is script.js loaded first?');
    })();
  }

  /* script.js never calls renderCart() on page load — every call site sits inside
     an event handler. So on a fresh load the drawer keeps the static "empty bag"
     markup from theme.liquid even when the cart has items, and there is no
     .cart-footer for the bar to attach to.

     Hydrating only when item_count > 0 fixes that and leaves the richer static
     empty state (with its perks block) untouched for genuinely empty carts. */
  async function hydrate() {
    try {
      const cart = await getCart();
      if (cart.item_count > 0) await window.renderCart(); // wrapped → decorates
    } catch (e) {
      console.warn('[otc-free-gift] hydrate failed:', e.message);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
